import { CalculateOpticalCenter } from "./types";

// Calculates the optical center (true camera position).
// Based on the formula: C = -Rᵀ · t
// This is used to convert a translation vector into the camera center.
export const calculateOpticalCenter: CalculateOpticalCenter = ({
  rotation,
  translation,
}) => {
  const [rx, ry, rz] = rotation;
  const [tx, ty, tz] = translation;

  // Compute the rotation angle from the rotation vector
  const angle = Math.sqrt(rx * rx + ry * ry + rz * rz);

  // If the angle is very small, the rotation is effectively the identity
  if (angle < 1e-8) {
    return [-tx, -ty, -tz];
  }

  // Normalize the rotation axis
  const axis = [rx / angle, ry / angle, rz / angle];
  const [ax, ay, az] = axis;

  // Construct the rotation matrix using Rodrigues' formula:
  // R = I + sin(θ)[k]× + (1 − cos(θ))[k]×²
  const cosAngle = Math.cos(angle);
  const sinAngle = Math.sin(angle);
  const oneMinusCos = 1 - cosAngle;

  // Rotation matrix elements
  const R11 = cosAngle + ax * ax * oneMinusCos;
  const R12 = ax * ay * oneMinusCos - az * sinAngle;
  const R13 = ax * az * oneMinusCos + ay * sinAngle;
  const R21 = ay * ax * oneMinusCos + az * sinAngle;
  const R22 = cosAngle + ay * ay * oneMinusCos;
  const R23 = ay * az * oneMinusCos - ax * sinAngle;
  const R31 = az * ax * oneMinusCos - ay * sinAngle;
  const R32 = az * ay * oneMinusCos + ax * sinAngle;
  const R33 = cosAngle + az * az * oneMinusCos;

  // Compute C = -Rᵀ · t (transpose of R multiplied by -t)
  const cx = -(R11 * tx + R21 * ty + R31 * tz);
  const cy = -(R12 * tx + R22 * ty + R32 * tz);
  const cz = -(R13 * tx + R23 * ty + R33 * tz);

  return [cx, cy, cz];
};
